import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import emailChanges from '../../../../data/emailChanges';
import { timestampToDate } from '../../../../utils/firebase-timestamp-transformation';
import './PayslipsTab.scss';

const PayslipsTab = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [migratedCount, setMigratedCount] = useState(0);

  useEffect(() => {
    fetchPayslips();
  }, []);

  const fetchPayslips = async () => {
    try {
      setLoading(true);
      
      // Access the payslips collection
      const payslipsCollectionRef = collection(db, 'payslips');
      const querySnapshot = await getDocs(payslipsCollectionRef);
      
      const payslipsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setPayslips(payslipsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching payslips:', error);
      setLoading(false);
    }
  };

  // Function to find new email for an employee from emailChanges.js
  const findNewEmail = (oldEmail) => {
    const change = emailChanges.find(change => change.oldEmail === oldEmail);
    return change ? change.newEmail : null;
  };

  // Handle migrate all payslips
  const handleMigrateAll = async () => {
    if (!window.confirm(`Are you sure you want to migrate all payslips? This will update email addresses in the payslips collection.`)) {
      return;
    }
    
    try {
      setMigrating(true);
      
      // Find payslips that need migration
      const payslipsToMigrate = payslips.filter(payslip => {
        // Skip already migrated payslips
        if (payslip.migrated) return false;
        
        // Check if email has mapping
        return payslip.email && findNewEmail(payslip.email);
      });
      
      setProgress({ current: 0, total: payslipsToMigrate.length });
      
      // Initialize batch
      let batch = writeBatch(db);
      let batchCount = 0;
      let totalMigrated = 0;
      
      // Process payslips in batches (Firestore has a limit of 500 operations per batch)
      for (let i = 0; i < payslipsToMigrate.length; i++) {
        const payslip = payslipsToMigrate[i];
        const oldEmail = payslip.email;
        const newEmail = findNewEmail(oldEmail);
        
        if (newEmail) {
          const payslipRef = doc(db, 'payslips', payslip.id);
          
          batch.update(payslipRef, {
            email: newEmail,
            oldEmail: oldEmail,
            migrated: true,
            migratedAt: new Date()
          });
          
          batchCount++;
          totalMigrated++;
          
          // If we've reached the batch limit, commit and start a new batch
          if (batchCount >= 450) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
        }
        
        // Update progress
        setProgress({ current: i + 1, total: payslipsToMigrate.length });
      }
      
      // Commit any remaining updates
      if (batchCount > 0) {
        await batch.commit();
      }
      
      setMigratedCount(totalMigrated);
      alert(`Successfully migrated ${totalMigrated} payslip records.`);
      
      // Refresh the list
      fetchPayslips();
      
    } catch (error) {
      console.error('Error migrating payslips:', error);
      alert(`Error migrating payslips: ${error.message}`);
    } finally {
      setMigrating(false);
    }
  };

  const filteredPayslips = searchTerm 
    ? payslips.filter(payslip => 
        payslip.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payslip.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payslip.empID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payslip.payMonth?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : payslips;

  // Format date for display using the utility function
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const convertedDate = timestampToDate(date);
    return convertedDate ? convertedDate.toLocaleDateString() : 'N/A';
  };

  // Format pay month for display
  const formatPayMonth = (payMonth) => {
    if (!payMonth) return 'N/A';
    
    try {
      // If it's in format "2025-FEB", convert to "February 2025"
      const parts = payMonth.split('-');
      if (parts.length === 2) {
        const year = parts[0];
        const monthCode = parts[1].toUpperCase();
        
        const monthNames = {
          'JAN': 'January',
          'FEB': 'February',
          'MAR': 'March',
          'APR': 'April',
          'MAY': 'May',
          'JUN': 'June',
          'JUL': 'July',
          'AUG': 'August',
          'SEP': 'September',
          'OCT': 'October',
          'NOV': 'November',
          'DEC': 'December'
        };
        
        const monthName = monthNames[monthCode] || monthCode;
        return `${monthName} ${year}`;
      }
      
      return payMonth;
    } catch (error) {
      return payMonth;
    }
  };

  // Extract filename from URL
  const getFileName = (fileUrl) => {
    if (!fileUrl) return 'N/A';
    try {
      // Extract filename from Firebase Storage URL
      const urlParts = fileUrl.split('/');
      const fileWithParams = urlParts[urlParts.length - 1];
      const fileName = fileWithParams.split('?')[0];
      // Decode URI component to handle special characters
      return decodeURIComponent(fileName);
    } catch (error) {
      return fileUrl.substring(fileUrl.lastIndexOf('/') + 1, fileUrl.indexOf('?'));
    }
  };

  // Count total number of migratable payslips
  const getMigratableCounts = () => {
    const total = payslips.length;
    const alreadyMigrated = payslips.filter(payslip => payslip.migrated).length;
    const canBeMigrated = payslips.filter(payslip => 
      !payslip.migrated && 
      payslip.email && 
      findNewEmail(payslip.email)
    ).length;
    const noMapping = total - alreadyMigrated - canBeMigrated;
    
    return { total, alreadyMigrated, canBeMigrated, noMapping };
  };

  const counts = getMigratableCounts();

  return (
    <div className="payslips-tab">
      <div className="payslips-header">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by email, employee ID, or pay month..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="refresh-container">
          <button 
            className="refresh-button"
            onClick={fetchPayslips}
            disabled={loading || migrating}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      <div className="migration-control">
        <div className="stats">
          <div className="stat-item">
            <span className="stat-label">Total Payslips:</span>
            <span className="stat-value">{counts.total}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Already Migrated:</span>
            <span className="stat-value">{counts.alreadyMigrated}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Ready to Migrate:</span>
            <span className="stat-value">{counts.canBeMigrated}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">No Email Mapping:</span>
            <span className="stat-value">{counts.noMapping}</span>
          </div>
        </div>
        
        <button 
          className="migrate-all-button"
          onClick={handleMigrateAll}
          disabled={loading || migrating || counts.canBeMigrated === 0}
        >
          {migrating
            ? 'Migrating...'
            : `Migrate ${counts.canBeMigrated} Payslip${counts.canBeMigrated === 1 ? '' : 's'}`}
        </button>
      </div>
      
      {migrating && (
        <div className="progress-bar-container">
          <div className="progress-text">
            Migrating {progress.current} of {progress.total} payslips...
          </div>
          <div className="progress-bar-wrapper">
            <div 
              className="progress-bar" 
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
      
      {migratedCount > 0 && !migrating && (
        <div className="success-message">
          Successfully migrated {migratedCount} payslip records in the last operation.
        </div>
      )}
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading payslip records...</p>
        </div>
      ) : filteredPayslips.length > 0 ? (
        <div className="payslips-table-container">
          <table className="payslips-table">
            <thead>
              <tr>
                <th>Employee Email</th>
                <th>Employee ID</th>
                <th>Pay Month</th>
                <th>Created At</th>
                <th>Document</th>
                <th>Migration Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayslips.map(payslip => (
                <tr key={payslip.id} className={payslip.migrated ? 'migrated-row' : ''}>
                  <td className="employee-email">
                    {payslip.email || 'N/A'}
                    {payslip.migrated && (
                      <div>
                        <span className="migrated-badge">Migrated</span>
                        <div className="old-email">Old: {payslip.oldEmail}</div>
                      </div>
                    )}
                  </td>
                  <td>{payslip.empID || 'N/A'}</td>
                  <td>{formatPayMonth(payslip.payMonth)}</td>
                  <td>{formatDate(payslip.createdAt)}</td>
                  <td className="file-cell">
                    {payslip.url ? (
                      <a href={payslip.url} target="_blank" rel="noopener noreferrer">
                        View Payslip
                      </a>
                    ) : 'N/A'}
                  </td>
                  <td>
                    {payslip.migrated ? (
                      <span className="already-migrated">Migrated</span>
                    ) : findNewEmail(payslip.email) ? (
                      <span className="can-migrate">Ready to Migrate</span>
                    ) : (
                      <span className="no-mapping">No Mapping</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">
          <p>No payslip records found.</p>
        </div>
      )}
    </div>
  );
};

export default PayslipsTab; 